import { Component, input, model, OnInit, OnDestroy } from '@angular/core';
import { forkJoin, Subject, takeUntil } from 'rxjs';
import { ClientContract } from 'src/app/models/client-contract';
import { ClientInformation } from 'src/app/models/client-information';
import { DestinationEndpoint } from 'src/app/models/destination-endpoint';
import { IntegrationIdentifier } from 'src/app/models/integration-identifier';
import { User } from 'src/app/models/user';
import { ClientContractService } from 'src/app/services/client-contract.service';
import { ClientIntegrationService } from 'src/app/services/client-integration.service';
import { IdentifierService } from 'src/app/services/identifier.service';
import { ToastService } from 'src/app/services/toast.service';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LTSpinnerComponent } from 'src/app/features/lt-shared-ui/lt-info/components/lt-spinner/lt-spinner.component';
import { LTSwitchComponent } from 'src/app/features/lt-shared-ui/lt-button/components/lt-switch/lt-switch.component';
import { LTDateTimePickerComponent } from 'src/app/features/lt-input/lt-time-picker/components/lt-datetimepicker/lt-datetimepicker.component';
import { LTDatePickerComponent } from 'src/app/features/lt-input/lt-time-picker/components/lt-datepicker/lt-datepicker.component';
import { IntegrationMapping } from 'src/app/models/integration-mapping';
import { Integration } from 'src/app/models/integration';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-client-contracts',
  templateUrl: './client-contracts.component.html',
  styleUrls: ['./client-contracts.component.scss'],
  providers: [ClientIntegrationService],
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatGridListModule,
    MatInputModule,
    MatSelectModule,
    MatTooltipModule,
    LTSpinnerComponent,
    LTSwitchComponent,
    LTDateTimePickerComponent,
    LTDatePickerComponent,
  ],
})
export class ClientContractComponent implements OnInit, OnDestroy {

  // Constant variables
  client = input.required<ClientInformation>();
  integrations = input<Integration[]>([]);
  selectedIntegrationId = input<string>();
  integrationIdentifiers = model<IntegrationIdentifier[]>([]);
  clientContracts = model<ClientContract[]>([]);
  destinationEndpoints = input<DestinationEndpoint[]>([]);
  currentMappedDestinations: DestinationEndpoint[] = [];
  clientInfo: ClientInformation;

  componentDestroyed$ = new Subject<boolean>();
  contractIdentifier: IntegrationIdentifier = null;
  selectedContract: ClientContract = null;
  availableIdentifiers: IntegrationIdentifier[] = [];

  // Status Variables
  contractsLoading = true;
  addingContract = false;
  editingContract = false;
  identifierMismatch = false;
  reprocessingInvoicePeriods = false;
  finishedLoading = false;
  viewingContract = true;
  savingClientContract = false;
  disablingContract = false;
  enablingContract = false;
  editingContractStartDate = false;
  editingBillingStartDate = false;
  editingContractTermDate = false;
  editingContractReinstatedDate = false;
  originalContractStartDate: Date = null;
  originalContractTermDate: Date = null;
  originalContractReinstatedDate: Date = null;
  originalBillingStartDate: Date = null;
  currentCompletedField = '';

  constructor(
    private toastService: ToastService,
    private clientContractService: ClientContractService,
    private identifierService: IdentifierService,
    private clientIntegrationService: ClientIntegrationService,
    private router: Router,
    private user: User,
  ) { }

  ngOnInit(): void {
    // Only treat client information as existing if it has been saved (has an ID)
    this.clientInfo = this.client()?.clientInformationId ? new ClientInformation(this.client()) : null;
    this.setupIdentifiers();
  }

  setupIdentifiers(): void {
    if (this.integrationIdentifiers()?.length == 0 || this.clientInfo == null) {
      //this.viewingContract = false;
      this.contractsLoading = false;
    }
    else {
      if (this.integrationIdentifiers()?.length > 0) {
        this.availableIdentifiers = this.integrationIdentifiers().slice();
        this.availableIdentifiers = this.availableIdentifiers.filter(x => this.clientContracts().findIndex(c => c.identifierIdValue == x.identifierIdValue && c.destinationEndpointId == x.destinationId) < 0);
        this.contractsLoading = false;
      }
      else {
        this.contractsLoading = false;
      }

    }
  }


  ngOnDestroy() {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

  setCancel() {
    this.cancel();
    this.reloadContracts();
    this.savingClientContract = false;
    this.editingBillingStartDate = false;
    this.editingContractStartDate = false;
    this.editingContractTermDate = false;
    this.editingContractReinstatedDate = false;
    this.originalContractStartDate = null;
    this.originalContractTermDate = null;
    this.originalContractReinstatedDate = null;
    this.originalBillingStartDate = null;
  }

  cancel() {
    this.disablingContract = false;
    this.enablingContract = false;
    this.addingContract = false;
    this.editingContract = false;
    this.reprocessingInvoicePeriods = false;
    this.viewingContract = true;
    this.selectedContract = null;


    for (let i = 0; i < this.availableIdentifiers.length; i++) {
      for (let j = 0; j < this.clientContracts().length; j++) {
        if (this.availableIdentifiers[i].identifierIdValue == this.clientContracts()[j].identifierIdValue
          && this.availableIdentifiers[i].destinationId == this.clientContracts()[j].destinationEndpointId) {
          this.availableIdentifiers.splice(i, 1);
          i--;
          break;
        }
      }
    }
  }

  setAdding() {
    this.editingContractStartDate = true;
    this.editingBillingStartDate = true;
    this.viewingContract = false;
    this.savingClientContract = false;
    this.addingContract = true;
    this.currentCompletedField = '';
    this.selectedContract = {} as ClientContract;
    this.selectedContract.integrationMappings = [];
    this.originalContractStartDate = null;
    this.originalContractTermDate = null;
    this.originalContractReinstatedDate = null;
    this.originalBillingStartDate = null;
  }

  setEditing(c: ClientContract) {
    const updated = new ClientContract(c);

    this.selectedContract = updated;
    this.originalContractStartDate = updated.contractStartDate;
    this.originalContractTermDate = updated.contractTermDate;
    this.originalContractReinstatedDate = updated.contractReinstatedDate;
    this.originalBillingStartDate = updated.billingStartDate;
    this.editingContractStartDate = true;
    this.editingBillingStartDate = true;

    this.initializeContractVariables();
    
    //If identifier id is the same as current, re add to available identifiers
    for (const identifier of this.integrationIdentifiers()) {
      if (identifier.identifierIdValue == this.selectedContract.identifierIdValue) {
        this.availableIdentifiers.push(identifier);
      }
    }
    
    this.viewingContract = false;
    this.editingContract = true;
    this.savingClientContract = false;
  }

  goToLoans(c: ClientContract) {
    this.router.navigate(['/invoicing-report/invoiced-loans-report', c.clientContractId]);
  }

  goToMetrics(c: ClientContract) {
    this.router.navigate(['/invoicing-report/invoiced-metrics-report', c.clientContractId]);
  }

  selectionChangeHandler(event: any): void {
    this.selectedContract = event.selectedRows[0].dataItem;
  }

  selectContract(c: ClientContract): void {
    this.selectedContract = new ClientContract(c);
  }

  isNullOrEmptyString(x: any) {
    return x === null || x === undefined || x.trim() === '';
  }

  addClientContract(): void {
    this.savingClientContract = true;

    if (this.contractIdentifier == null || this.currentCompletedField == null) {
      this.toastService.toastCreate('Enter all contract information before adding.', 'Warning', {
        autoClose: false,
      });
      this.savingClientContract = false;
      return;
    }

    this.selectedContract.clientInformationId = this.clientInfo?.clientInformationId;
    this.selectedContract.updatedDate = new Date();
    this.selectedContract.losTalkerClientId = this.client().losTalkerClientId;
    this.selectedContract.clientShortName = this.client().compensafeInternalName;
    this.selectedContract.clientFullName = this.client().clientDisplayName;

    if (this.selectedContract.integrationMappings.findIndex(i => i.integrationId == this.contractIdentifier.integrationId) == -1) {
      const integrationMapping: IntegrationMapping = {
        clientContractId: this.selectedContract.clientContractId,
        integrationId: this.contractIdentifier.integrationId,
        integrationMappingId: '00000000-0000-0000-0000-000000000000',
      };

      this.selectedContract.integrationMappings.push(integrationMapping);
    }

    this.clientContractService.insertClientContract(this.selectedContract)
      .subscribe({
        next: () => {
          this.toastService.toastCreate('Client contract inserted', 'Success', {
            autoClose: true,
          });

          this.cancel();
          this.reloadContracts();
          this.savingClientContract = false;
          this.editingBillingStartDate = false;
          this.editingContractStartDate = false;
          this.editingContractTermDate = false;
          this.editingContractReinstatedDate = false;
        },
        error: (_error) => {
          this.toastService.toastCreate('Failed to add client contract.', 'Danger', {
            autoClose: false,
          });
          this.savingClientContract = false;
        },
      });
  }

  getMappedIntegrationsDisplay(mappedIntId: IntegrationMapping[]): string {
    if (mappedIntId != null && mappedIntId.length == 1) {
      const selectedInt = this.integrations().find(i => i.integrationId === mappedIntId[0].integrationId);
      if (selectedInt != null) {
        return selectedInt.name;
      }
    }
    else if (mappedIntId != null && mappedIntId.length > 1) {
      return mappedIntId.length + ' Integrations';
    }
    return 'Not Mapped';
  }

  getIntegrationName(integrationId: string) {
    const selectedInt = this.integrations().find(i => i.integrationId === integrationId);
    if (selectedInt != null) {
      return selectedInt.name;
    }
  }

  addMapping(c: ClientContract): void {
    this.savingClientContract = true;
    this.selectedContract = new ClientContract(c);

    if (this.selectedIntegrationId() != undefined) {

      if (this.selectedContract.integrationMappings != undefined && this.selectedContract.integrationMappings.find(i => i.integrationId === this.selectedIntegrationId()) != undefined) {
        this.toastService.toastCreate('Integration Mapping for Current Integration already exists. No new mapping added.', 'Warning', {
          autoClose: false,
        });
      }
      else {

        const mapping: IntegrationMapping = {
          integrationMappingId: '00000000-0000-0000-0000-000000000000',
          integrationId: this.selectedIntegrationId(),
          clientContractId: this.selectedContract.clientContractId,
        };

        this.clientContractService.insertIntegrationMapping(mapping).subscribe(() => {
          this.cancel();
          this.reloadContracts();
          this.savingClientContract = false;
          this.editingBillingStartDate = false;
          this.editingContractStartDate = false;
          this.editingContractTermDate = false;
          this.editingContractReinstatedDate = false;
        });
      }
    }
    else {
      this.toastService.toastCreate('Integration not Correctly passed in to Client Contract component', 'Warning', {
        autoClose: false,
      });
    }
  }

  deleteIntegrationMapping(integrationMap: IntegrationMapping) {
    this.savingClientContract = true;
    this.clientContractService.deleteIntegrationMapping(integrationMap).subscribe(() => {
      this.selectedContract.integrationMappings = this.selectedContract.integrationMappings.filter(i => i.integrationId !== integrationMap.integrationId);
      this.savingClientContract = false;
      this.clientContracts.update(arr => {
        const idx = arr.findIndex(c => c.clientContractId == this.selectedContract.clientContractId);
        if (idx !== -1) { const updated = [...arr]; updated[idx] = this.selectedContract; return updated; }
        return arr;
      });

      this.toastService.toastCreate('Integration mapping deleted for: ' + integrationMap.integrationId, 'Success', {
        autoClose: true,
      });
    });
  }

  updateClientContract(): void {
    this.savingClientContract = true;

    if (this.contractIdentifier == null || this.currentCompletedField == null) {
      this.toastService.toastCreate('Required Contract fields not populated', 'Warning', {
        autoClose: false,
      });
      this.savingClientContract = false;
      return;
    }

    forkJoin({
      identifier: this.identifierService.updateIntegrationIdentifier(this.contractIdentifier),
      contractReturn: this.clientContractService.updateClientContract(this.selectedContract),
    })
      .subscribe({
        // eslint-disable-next-line unused-imports/no-unused-vars
        next: ({ identifier, contractReturn }) => {
          this.toastService.toastCreate('Client contract updated.', 'Success', {
            autoClose: true,
          });

          this.cancel();
          this.reloadContracts();
          this.savingClientContract = false;
          this.editingBillingStartDate = false;
          this.editingContractStartDate = false;
          this.editingContractTermDate = false;
          this.editingContractReinstatedDate = false;
        },
        error: (_error) => {
          this.toastService.toastCreate('Failed to update client contract.', 'Danger', {
            autoClose: false,
          });
        },
      });
  }

  reloadContracts(): void {
    this.contractsLoading = true;

    forkJoin({
      identifiers: (this.identifierService.getIntegrationIdentifiersByClientInformation(this.client().clientInformationId)),
      contracts: (this.clientContractService.getAllClientContractsByClientInfo(this.client().clientInformationId)),
    })
      .subscribe(({ identifiers, contracts }) => {
        this.clientContracts.set(contracts);
        this.integrationIdentifiers.set(identifiers);
        this.setupIdentifiers();
      });
  }

  setEnabling(c: ClientContract): void {
    this.selectedContract = new ClientContract(c);
    this.viewingContract = false;
    this.disablingContract = false;
    this.enablingContract = true;
    this.savingClientContract = false;

    this.initializeContractVariables();
  }

  enableContract(): void {
    this.savingClientContract = true;
    this.clientContractService.undeleteClientContract(this.selectedContract).subscribe(() => {
      this.cancel();
      this.reloadContracts();
      this.savingClientContract = false;
    });
  }

  setReprocessInvoicePeriods(c: ClientContract): void {
    if (c.productionInvoiceReport == true) {
      this.toastService.toastCreate('Contract is a Production Invoice. Reprocessing Invoice Periods is not allowed.', 'Danger');
    }
    else {
      this.selectedContract = c;
      this.viewingContract = false;
      this.reprocessingInvoicePeriods = true;

      this.initializeContractVariables();
    }
  }

  reprocessInvoicePeriods(): void {
    this.savingClientContract = true;
    this.clientContractService.setRecreateInvoicesForAuditPeriod(this.selectedContract.clientContractId, true).subscribe(() => {
      this.selectedContract.recreateInvoicesForAuditPeriod = true;
      this.cancel();
      this.reloadContracts();
      this.savingClientContract = false;
    });
  }

  setDisabling(c: ClientContract): void {
    this.selectedContract = new ClientContract(c);
    this.viewingContract = false;
    this.disablingContract = true;
    this.enablingContract = false;
    this.savingClientContract = false;

    this.initializeContractVariables();
  }

  disableContract(): void {
    this.savingClientContract = true;
    this.clientContractService.deleteClientContract(this.selectedContract).subscribe(() => {
      this.cancel();
      this.reloadContracts();
      this.savingClientContract = false;
    });
  }

  getDestinationEndpointString(destinationId): string {
    let str = 'Destination Endpoint Error: Destination Not Found';
    for (const destination of this.destinationEndpoints()) {
      if (destination.destinationEndpointId == destinationId) {
        str = destination.endpointName;
      }
    }
    return str;
  }

  checkAccess(role: string[]): boolean {
    return this.user.checkAccess(role);
  }

  initializeContractVariables(): void {
    this.selectedContract.clientInformationId = this.clientInfo?.clientInformationId;
    this.selectedContract.losTalkerClientId = this.client().losTalkerClientId;
    this.selectedContract.clientShortName = this.client().compensafeInternalName;
    this.selectedContract.clientFullName = this.client().clientDisplayName;
    this.selectedContract.endpointName = this.destinationEndpoints().find(d => d.destinationEndpointId === this.selectedContract.destinationEndpointId).endpointName;

    for (const identifier of this.integrationIdentifiers()) {
      if (identifier.identifierIdValue === this.selectedContract.identifierIdValue
        && identifier.destinationId === this.selectedContract.destinationEndpointId) {
        this.contractIdentifier = identifier;
        break;
      }
    }



    this.currentCompletedField = this.selectedContract.currentCompletedField;
  }

  isCurrentIntegrationMapped(c: ClientContract): boolean {
    let rc = false;
    if (c.integrationMappings != undefined && this.selectedIntegrationId() != undefined) {
      rc = c.integrationMappings.find(i => i.integrationId === this.selectedIntegrationId()) != undefined;
    }

    return rc;
  }

  selectNewIdentifier(): void {
    //this.contractIdentifier = event;
    this.selectedContract.identifierNameValue = this.contractIdentifier.identifierNameValue;
    this.selectedContract.identifierKeyValue = this.contractIdentifier.identifierKeyValue;
    this.selectedContract.identifierIdValue = this.contractIdentifier.identifierIdValue;
    this.selectedContract.destinationEndpointId = this.contractIdentifier.destinationId;
    this.selectedContract.endpointName = this.contractIdentifier.destinationName;
  }
}

